import React, { useState, useEffect } from 'react';
import { getForecastAnalysis } from '../api';
import ForecastStocks from './ForecastStocks';
import ForecastStock from './ForecastStock';

/**
 * Сторінка /forecasts — «Прогнози» (підпроєкт forecasts, лише адмін).
 *
 * Три вкладки: «Акції» (таблиця всесвіту з «Резюме» і консенсусом),
 * «Акція» (графік, індикатори, консенсус) і «Аналіз» — чи справджується
 * «Резюме» теханалізу investing.com на 10-річній історії. Вкладки —
 * Analysis (тут), ForecastStocks.js і ForecastStock.js.
 *
 * Головне число дослідження — ПЕРЕВИЩЕННЯ НАД РИНКОМ: від дохідності паперу
 * віднімається середня дохідність усіх відібраних паперів того самого дня.
 * Абсолютні числа показуємо поруч, бо реальна стратегія отримує саме їх —
 * і на денному таймфреймі «активно купувати» в абсолютних числах найгірший стан.
 */

const STATES = [
  { id: 'strong_sell', label: 'Активно продавати', color: '#C02B5E', bg: '#FDE8EF' },
  { id: 'sell',        label: 'Продавати',         color: '#C2410C', bg: '#FFEDD5' },
  { id: 'neutral',     label: 'Нейтрально',        color: '#5B646F', bg: '#F1F3F5' },
  { id: 'buy',         label: 'Купувати',          color: '#15803D', bg: '#DCFCE7' },
  { id: 'strong_buy',  label: 'Активно купувати',  color: '#007A41', bg: '#E7F7EF' },
];
const STATE_BY_ID = Object.fromEntries(STATES.map((s) => [s.id, s]));

// Колонки головної таблиці. `signed` — фарбувати за знаком (перевищення над ринком).
const COLS = [
  { key: 'ret5',       label: 'Тиждень',         unit: '%' },
  { key: 'ret21',      label: 'Місяць',          unit: '%' },
  { key: 'ret63',      label: '3 міс',           unit: '%' },
  { key: 'ret63_med',  label: '3 міс, медіана',  unit: '%' },
  { key: 'x_ret21',    label: 'Понад ринок 1м',  unit: '%', signed: true },
  { key: 'x_ret63',    label: 'Понад ринок 3м',  unit: '%', signed: true },
  { key: 'tp_hit',     label: '+10% за 3 міс',   unit: '%', share: true },
  { key: 'tp_ret',     label: 'Правило +10%/3м', unit: '%' },
  { key: 'x_tp_ret',   label: 'Правило понад ринок', unit: '%', signed: true },
  { key: 'miss_ret63', label: 'Не дійшли до +10%', unit: '%' },
  { key: 'rows',       label: 'Днів-сигналів',   int: true },
  { key: 'tickers',    label: 'Паперів',         int: true },
];

const YEAR_METRICS = [
  { key: 'x_tp_ret', label: 'Правило +10%/3м понад ринок', signed: true },
  { key: 'tp_ret',   label: 'Правило +10%/3м, абсолютно' },
  { key: 'x_ret63',  label: '3 міс понад ринок', signed: true },
  { key: 'ret63',    label: '3 міс, абсолютно' },
  { key: 'tp_hit',   label: 'Частка «+10% за 3 міс»', share: true },
];

// Висновки — інтерпретація чисел (forecasts/study/RESULTS.md), тому окремо
// для кожного таймфрейму, а не обчислюються тут.
const FINDINGS = {
  '1h': [
    'Годинне «Резюме» нічого не передбачає на горизонті від тижня до 3 місяців: перевищення над ринком у всіх станах у межах ±0.11%, без порядку від «продавати» до «купувати». Свіжий сигнал — те саме.',
    '«+10% за 3 місяці» досягають 59–63% паперів незалежно від сигналу — це наслідок волатильності, а не прогнозу.',
    'Правило «+10% або продаж через 3 місяці» дає ~+0.7…0.9% на угоду в будь-якому стані (без комісій і проковзування).',
  ],
  '1d': [
    'Слабкий слід лише в крайніх станах: «активно купувати» +0.13% понад ринок на угоду правила, «активно продавати» −0.13%. Це менше за витрати на угоду (≈0.1–0.2% за коло).',
    'Свіжий сигнал «активно купувати» (стан щойно змінився) — нуль.',
    'В абсолютних числах «активно купувати» — найгірший стан: правило +0.51% на угоду проти +1.10% у «активно продавати». Стратегія отримує абсолютну дохідність, тож купуючи за цим сигналом, заробляє менше, ніж купуючи навмання.',
    '«+10% за 3 місяці» — 58–65% у всіх станах, як і на годинному.',
  ],
};

function fmt(v, col) {
  if (v === null || v === undefined) return '—';
  if (col.int) return v.toLocaleString('uk-UA');
  const s = col.share ? v.toFixed(1) : v.toFixed(2);
  return `${col.signed && v > 0 ? '+' : ''}${s}${col.unit === '%' || col.share ? '%' : ''}`;
}

// Колір для перевищення над ринком: ±0.3% — повна насиченість.
function signedStyle(v) {
  if (v === null || v === undefined || Math.abs(v) < 0.005) return {};
  const a = Math.min(Math.abs(v) / 0.3, 1);
  return v > 0
    ? { background: `rgba(0, 166, 86, ${0.06 + 0.22 * a})`, color: '#006B37' }
    : { background: `rgba(192, 43, 94, ${0.06 + 0.22 * a})`, color: '#A3214D' };
}

function StateBadge({ id }) {
  const st = STATE_BY_ID[id];
  if (!st) return <b>Усі стани</b>;
  return <span style={{ ...S.badge, background: st.bg, color: st.color }}>{st.label}</span>;
}

function MainTable({ rows }) {
  const order = [...STATES.map((s) => s.id), 'all'];
  const sorted = order.map((id) => rows.find((r) => r.state === id)).filter(Boolean);
  return (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={{ ...S.th, textAlign: 'left' }}>Стан «Резюме»</th>
            {COLS.map((c) => <th key={c.key} style={S.th}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.state} style={r.state === 'all' ? S.trBase : S.tr}>
              <td style={S.td}><StateBadge id={r.state} /></td>
              {COLS.map((c) => (
                <td key={c.key} style={{ ...S.td, textAlign: 'right',
                                         ...(c.signed && r.state !== 'all' ? signedStyle(r[c.key]) : {}) }}>
                  {fmt(r[c.key], c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function YearTable({ rows, metric }) {
  const col = { ...metric, unit: '%' };
  return (
    <div style={{ ...S.tableWrap, maxWidth: 900 }}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={{ ...S.th, textAlign: 'left' }}>Рік</th>
            {STATES.map((s) => <th key={s.id} style={S.th}>{s.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.year} style={S.tr}>
              <td style={{ ...S.td, fontWeight: 600 }}>{r.year}</td>
              {STATES.map((s) => (
                <td key={s.id} style={{ ...S.td, textAlign: 'right',
                                        ...(metric.signed ? signedStyle(r[s.id]) : {}) }}>
                  {fmt(r[s.id], col)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Analysis() {
  const [timeframe, setTimeframe] = useState('1d');
  const [fresh,     setFresh]     = useState(false);
  const [yearKey,   setYearKey]   = useState('x_tp_ret');
  const [cache,     setCache]     = useState({});
  const [error,     setError]     = useState('');

  useEffect(() => {
    if (cache[timeframe]) return;
    let alive = true;
    setError('');
    getForecastAnalysis(timeframe)
      .then((res) => { if (alive) setCache((c) => ({ ...c, [timeframe]: res.data })); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || String(e)); });
    return () => { alive = false; };
  }, [timeframe, cache]);

  const d = cache[timeframe];
  const metric = YEAR_METRICS.find((m) => m.key === yearKey);

  return (
    <div>
      <p style={S.lead}>
        Що було з акцією після кожного стану «Резюме» теханалізу investing.com, який ми рахуємо самі
        з барів. Сигнал знято на закритті дня, вхід — за відкриттям наступного. Лише прості акції,
        торги за день ≥ $1 млн, ціна ≥ $1, мертві компанії включено. Правило «+10%/3м»: продаж за +10%,
        якщо ціна його досягла протягом 63 торгових днів, інакше — на закритті 63-го дня.
        «Понад ринок» — мінус середня дохідність усіх відібраних паперів того самого дня.
      </p>

      <div style={S.controls}>
        {[['1h', 'Годинне «Резюме»'], ['1d', 'Денне «Резюме»']].map(([id, label]) => (
          <button key={id} onClick={() => setTimeframe(id)}
                  style={{ ...S.tab, ...(timeframe === id ? S.tabActive : {}) }}>{label}</button>
        ))}
        <span style={{ width: 16 }} />
        {[[false, 'Усі дні'], [true, 'Лише свіжий сигнал']].map(([v, label]) => (
          <button key={label} onClick={() => setFresh(v)}
                  style={{ ...S.tab, ...(fresh === v ? S.tabActive : {}) }}>{label}</button>
        ))}
      </div>

      {error && <div style={S.err}>{error}</div>}
      {!d && !error && <div style={S.info}>Завантаження…</div>}

      {d && (<>
        <div style={S.meta}>
          Сигнали {d.date_from} … {d.date_to} · {d.rows.toLocaleString('uk-UA')} днів-сигналів ·
          {' '}{d.tickers.toLocaleString('uk-UA')} паперів (мертвих {d.dead_tickers.toLocaleString('uk-UA')}) ·
          {' '}звіт від {d.built_at}
          {fresh && ' · свіжий = стан змінився саме цього дня'}
        </div>

        <MainTable rows={fresh ? d.fresh : d.all} />
        <div style={S.note}>
          Рядки по днях перекриваються (63-денні вікна сусідніх днів майже однакові), тож кількість
          днів-сигналів — не кількість незалежних спостережень. Хвости обрізано до −100% … +300%.
        </div>

        <h3 style={S.h3}>Висновки</h3>
        <ul style={S.list}>
          {(FINDINGS[timeframe] || []).map((t) => <li key={t} style={{ marginBottom: 6 }}>{t}</li>)}
        </ul>

        <h3 style={S.h3}>За роками</h3>
        <div style={S.meta}>Рік — за датою сигналу; усі дні, не лише свіжі.</div>
        <div style={S.controls}>
          {YEAR_METRICS.map((m) => (
            <button key={m.key} onClick={() => setYearKey(m.key)}
                    style={{ ...S.tab, ...(yearKey === m.key ? S.tabActive : {}) }}>{m.label}</button>
          ))}
        </div>
        <YearTable rows={d.by_year[yearKey] || []} metric={metric} />
      </>)}
    </div>
  );
}

const TABS = [
  { id: 'stocks',   label: 'Акції' },
  { id: 'stock',    label: 'Акція' },
  { id: 'analysis', label: 'Аналіз' },
];
const TAB_KEY = 'forecasts.tab';

export default function Forecasts() {
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem(TAB_KEY) || 'analysis'; } catch { return 'analysis'; }
  });
  const [ticker, setTicker] = useState(null);
  const choose = (id) => {
    setTab(id);
    try { localStorage.setItem(TAB_KEY, id); } catch { /* приватне вікно */ }
  };
  const [at, setAt] = useState({ date: '', hour: 16 });
  const openStock = (t, date, hour) => { setTicker(t); setAt({ date: date || '', hour: hour || 16 }); choose('stock'); };

  return (
    <div style={S.wrap}>
      <h2 style={S.h2}>Прогнози</h2>
      <div style={{ ...S.controls, marginBottom: 18 }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => choose(t.id)}
                  style={{ ...S.viewTab, ...(tab === t.id ? S.viewTabActive : {}) }}>
            {t.id === 'stock' && ticker ? `Акція: ${ticker}` : t.label}
          </button>
        ))}
      </div>
      {tab === 'analysis' && <Analysis />}
      {tab === 'stocks' && <ForecastStocks onOpenStock={openStock} />}
      {tab === 'stock' && <ForecastStock ticker={ticker} at={at} onTicker={setTicker} />}
    </div>
  );
}

const S = {
  // Палітра кабінету — та сама, що в Marginality.
  wrap:  { maxWidth: 1400, margin: '0 auto', padding: '24px 16px', color: '#1A1A1A' },
  h2:    { margin: '0 0 12px', fontSize: 22, color: '#1A1A1A' },
  h3:    { margin: '24px 0 6px', fontSize: 16, color: '#1A1A1A' },
  lead:  { margin: '0 0 14px', color: '#5B646F', lineHeight: 1.5, maxWidth: 980, fontSize: 14 },
  meta:  { margin: '0 0 10px', fontSize: 13, color: '#5B646F' },
  note:  { margin: '8px 0 0', fontSize: 12, color: '#8A929C', maxWidth: 980 },
  list:  { margin: '0 0 8px', paddingLeft: 20, color: '#1A1A1A', lineHeight: 1.5, maxWidth: 980,
           fontSize: 14 },
  controls: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 },
  tab:   { padding: '6px 12px', borderRadius: 6, border: '1px solid #E5E7EB',
           background: '#FFFFFF', color: '#5B646F', cursor: 'pointer', fontSize: 13 },
  tabActive: { background: '#475FFA', border: '1px solid #475FFA', color: '#fff' },
  viewTab: { padding: '8px 16px', borderRadius: 8, border: '1px solid #E5E7EB',
             background: '#FFFFFF', color: '#5B646F', cursor: 'pointer',
             fontSize: 14, fontWeight: 500 },
  viewTabActive: { background: '#101220', border: '1px solid #101220', color: '#fff' },
  tableWrap: { overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: 8,
               background: '#FFFFFF' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:    { padding: '10px 12px', background: '#F9FAFB', color: '#5B646F', textAlign: 'right',
           whiteSpace: 'nowrap', borderBottom: '1px solid #E5E7EB', fontWeight: 600 },
  tr:    { borderBottom: '1px solid #F1F3F5' },
  trBase: { borderTop: '2px solid #E5E7EB', background: '#F9FAFB' },
  td:    { padding: '8px 12px', color: '#1A1A1A', whiteSpace: 'nowrap',
           fontVariantNumeric: 'tabular-nums' },
  badge: { padding: '2px 8px', borderRadius: 4, fontSize: 12, whiteSpace: 'nowrap', fontWeight: 500 },
  info:  { padding: 16, color: '#5B646F' },
  err:   { padding: 12, background: '#FDE8EF', border: '1px solid #F5C2D3',
           borderRadius: 6, color: '#C02B5E', marginBottom: 12 },
};
